import * as React from "react";
import { UserContext } from "../../../contexts/user.context";
import { Get, Patch, Post, handleError } from "../../../utils/fetch.util";
import { t } from "i18next";
import Select from "react-select";
import {
  AddButton,
  ButtonsContainer,
  CancelButton,
} from "../addClientModal/addClientModal.styles";
import { toast } from "react-toastify";

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getAllItems: () => void;
  editSalaryRecord: any;
  setEditSalaryRecord: React.Dispatch<any>;
}

interface SalaryRecord {
  staff_id: number;
  bonus: number;
  deduction: number;
  month: string;
}

const AddEditSalaryRecord: React.FC<Props> = ({
  open,
  setOpen,
  editSalaryRecord,
  setEditSalaryRecord,
  getAllItems,
}) => {
  const defaultSalaryRecord = {
    staff_id: 0,
    bonus: 0,
    deduction: 0,
    month: new Date().toISOString().split("T")[0].slice(0, 7),
  };
  const [salaryRecord, setSalaryRecord] =
    React.useState<SalaryRecord>(defaultSalaryRecord);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { getValidToken } = React.useContext(UserContext);
  const [staff, setStaff] = React.useState<any[]>([]);

  const staffOptions =
    staff.length > 0
      ? staff.map((item) => ({
          label: item.asdc_client_staff_name,
          value: item.asdc_client_staff_id,
        }))
      : [];

  const getAllStaff = async () => {
    setIsLoading(true);
    const res = await Get("asdc-client-staff/get-all-staff", getValidToken());

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setStaff(res.data);
  };

  const handleAdd = async (e: any) => {
    e.preventDefault();
    const res = await Post(
      "staff-salary/add-salary-record",
      { ...salaryRecord, month: +salaryRecord.month.split("-")[1] },
      getValidToken(),
    );

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }

    toast.success("Successfully Added");
    setSalaryRecord(defaultSalaryRecord);
    setIsLoading(false);
    getAllItems();
    setOpen(false);
  };

  const handleEdit = async (e: any) => {
    e.preventDefault();

    const res = await Patch(
      `staff-salary/edit-salary-record?salary_record_id=${editSalaryRecord.id}&bonus=${salaryRecord.bonus}&deduction=${salaryRecord.deduction}&staff_id=${salaryRecord.staff_id}`,
      {},
      getValidToken(),
    );

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }

    toast.success("Successfully Edited");
    setSalaryRecord(defaultSalaryRecord);
    setIsLoading(false);
    getAllItems();
    setOpen(false);
    setEditSalaryRecord(null);
  };

  React.useEffect(() => {
    getAllStaff();
  }, []);

  React.useEffect(() => {
    editSalaryRecord &&
      setSalaryRecord({
        staff_id: editSalaryRecord.staff_id,
        bonus: editSalaryRecord.bonus,
        deduction: editSalaryRecord.deduction,
        month: editSalaryRecord.month,
      });
  }, [editSalaryRecord]);

  return (
    <>
      <div
        className={`toggle_server_model fixed top-0 left-0 w-full h-full bg-[#00000075] flex items-center justify-center !z-[99999] ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="fixed top-0 left-0 w-full h-[100vh] cursor-pointer"
          onClick={() => {
            setOpen(false);
            setSalaryRecord(defaultSalaryRecord);
            setEditSalaryRecord(null);
          }}
        ></div>

        <form
          className={`bg-white w-full max-w-[500px] p-6 relative z-[9999] rounded-lg`}
          onSubmit={editSalaryRecord ? handleEdit : handleAdd}
        >
          <h2 className="text-3xl mb-6 !text-black">
            {t(editSalaryRecord ? "edit_salary_record" : "add_salary_record")}
          </h2>

          <div className="flex flex-col gap-2 mt-5">
            <label>{t("deduction")}</label>
            <input
              onChange={(e) =>
                setSalaryRecord((prev) => {
                  return { ...prev, deduction: Number(e.target.value) };
                })
              }
              value={salaryRecord.deduction}
              name="deduction"
              className="border"
              type="number"
            />
          </div>
          <div className="flex flex-col gap-2 mt-5">
            <label>{t("bonus")}</label>
            <input
              onChange={(e) =>
                setSalaryRecord((prev) => {
                  return { ...prev, bonus: Number(e.target.value) };
                })
              }
              value={salaryRecord.bonus}
              name="bonus"
              className="border"
              type="number"
            />
          </div>
          {!editSalaryRecord && (
            <div className="flex flex-col gap-2 mt-5">
              <label aria-required>{t("month")}</label>
              <input
                onChange={(e) =>
                  setSalaryRecord((prev) => {
                    return { ...prev, month: e.target.value };
                  })
                }
                value={salaryRecord.month}
                name="month"
                className="border"
                type="month"
                required
              />
            </div>
          )}
          <div className="flex flex-col gap-2 mt-5">
            <label aria-required>{t("staff")}</label>
            <Select
              className="basic-single w-full"
              classNamePrefix="select"
              isSearchable={true}
              options={staffOptions}
              value={staffOptions.find(
                (item) => item.value == salaryRecord.staff_id,
              )}
              onChange={(result: any) => {
                setSalaryRecord((prev) => {
                  return {
                    ...prev,
                    staff_id: result.value,
                  };
                });
              }}
              placeholder="Select staff..."
            />
          </div>
          <ButtonsContainer style={{ gap: "2rem" }}>
            <AddButton isLoading={isLoading} type="submit">
              {t("submit")}
            </AddButton>
            <CancelButton
              type="button"
              style={{ border: "2px solid black" }}
              onClick={() => {
                setOpen(false);
                setSalaryRecord(defaultSalaryRecord);
                setEditSalaryRecord(null);
              }}
            >
              {t("cancelBtn")}
            </CancelButton>
          </ButtonsContainer>
        </form>
      </div>
    </>
  );
};

export default AddEditSalaryRecord;
